import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GripHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * A draggable card inside the meeting area (board m5gc #3/#4): the same
 * black rounded surface as the control bar, a grip to drag it by, and a
 * position that is clamped to the area and remembered for the session.
 *
 * Positioned `absolute` against its nearest positioned ancestor — the
 * meeting stage — so it can never be dragged under the sidebar or off the
 * page, and it re-clamps when the stage resizes (panel toggled, window
 * resized, phone rotated).
 *
 * The position is the card's **bottom-right corner** (distance from the
 * stage's right and bottom edges), not its top-left (board m5gc #18): a
 * card that changes size — the dock collapsing to an avatar row — shrinks
 * down towards that corner and grows back up and to the left, and a dragged
 * card keeps its bottom-right spot across collapse/expand.
 */

export interface CardPosition {
  /** Distance from the stage's right edge to the card's right edge. */
  right: number
  /** Distance from the stage's bottom edge to the card's bottom edge. */
  bottom: number
}

/** Where a card starts, as a distance from the stage's edges. */
export type CardAnchor = { left: number; bottom: number } | { right: number; bottom: number }

const MARGIN = 8

function readPosition(key: string): CardPosition | null {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CardPosition>
    if (typeof parsed.right !== 'number' || typeof parsed.bottom !== 'number') return null
    return { right: parsed.right, bottom: parsed.bottom }
  } catch {
    return null
  }
}

function writePosition(key: string, position: CardPosition): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(position))
  } catch {
    // Private mode or a full store: the card simply forgets on reload.
  }
}

export function clampPosition(
  position: CardPosition,
  stage: { width: number; height: number },
  card: { width: number; height: number }
): CardPosition {
  const maxRight = Math.max(MARGIN, stage.width - card.width - MARGIN)
  const maxBottom = Math.max(MARGIN, stage.height - card.height - MARGIN)
  return {
    right: Math.min(Math.max(MARGIN, position.right), maxRight),
    bottom: Math.min(Math.max(MARGIN, position.bottom), maxBottom),
  }
}

export function anchorToPosition(
  anchor: CardAnchor,
  stage: { width: number; height: number },
  card: { width: number; height: number }
): CardPosition {
  const right = 'right' in anchor ? anchor.right : stage.width - card.width - anchor.left
  return clampPosition({ right, bottom: anchor.bottom }, stage, card)
}

export function FloatingCard({
  storageKey,
  anchor,
  handleLabel,
  className,
  handleExtra,
  children,
}: {
  /** `sessionStorage` key — the position is per card, per browser session. */
  storageKey: string
  /** Where the card sits until it is dragged. */
  anchor: CardAnchor
  handleLabel: string
  className?: string
  /** Rendered at the right end of the grip bar (a status icon, a button). */
  handleExtra?: React.ReactNode
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<CardPosition | null>(null)
  /** While dragging: where the pointer sits relative to the card's bottom-right corner. */
  const drag = useRef<{ pointerId: number; dx: number; dy: number } | null>(null)

  const stageOf = useCallback(() => {
    const el = ref.current
    const stage = el?.offsetParent as HTMLElement | null
    if (!el || !stage) return null
    return {
      stage: { width: stage.clientWidth, height: stage.clientHeight },
      card: { width: el.offsetWidth, height: el.offsetHeight },
    }
  }, [])

  // First paint: the remembered spot, or the anchor. Then keep it inside the
  // stage whenever either of them changes size.
  useLayoutEffect(() => {
    const clamp = () => {
      const sizes = stageOf()
      if (!sizes) return
      setPosition((current) => {
        const wanted =
          current ?? readPosition(storageKey) ?? anchorToPosition(anchor, sizes.stage, sizes.card)
        const next = clampPosition(wanted, sizes.stage, sizes.card)
        return current && next.right === current.right && next.bottom === current.bottom
          ? current
          : next
      })
    }
    clamp()
    const observer = new ResizeObserver(clamp)
    if (ref.current) observer.observe(ref.current)
    if (ref.current?.offsetParent) observer.observe(ref.current.offsetParent)
    return () => observer.disconnect()
    // The anchor is a literal from the caller; a new object each render must
    // not restart the observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, stageOf])

  useEffect(() => {
    if (position) writePosition(storageKey, position)
  }, [position, storageKey])

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return
    const el = ref.current
    const stage = el?.offsetParent as HTMLElement | null
    if (!el || !stage || !position) return
    event.currentTarget.setPointerCapture(event.pointerId)
    // Pointer position measured from the stage's bottom-right corner, so a
    // move can be expressed in the same right/bottom terms as the card.
    const rect = stage.getBoundingClientRect()
    drag.current = {
      pointerId: event.pointerId,
      dx: rect.right - event.clientX - position.right,
      dy: rect.bottom - event.clientY - position.bottom,
    }
    event.preventDefault()
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = drag.current
    const stage = ref.current?.offsetParent as HTMLElement | null
    if (!current || current.pointerId !== event.pointerId || !stage) return
    const sizes = stageOf()
    if (!sizes) return
    const rect = stage.getBoundingClientRect()
    setPosition(
      clampPosition(
        {
          right: rect.right - event.clientX - current.dx,
          bottom: rect.bottom - event.clientY - current.dy,
        },
        sizes.stage,
        sizes.card
      )
    )
  }

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId !== event.pointerId) return
    drag.current = null
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  return (
    <div
      ref={ref}
      data-floating-card={storageKey}
      className={cn(
        'absolute z-40 flex flex-col overflow-hidden rounded-2xl border border-zinc-700 bg-zinc-900/95 text-white shadow-xl backdrop-blur',
        position ? 'visible' : 'invisible',
        className
      )}
      style={{ right: position?.right ?? 0, bottom: position?.bottom ?? 0 }}
    >
      <div
        role="button"
        aria-label={handleLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="flex h-7 shrink-0 cursor-grab touch-none items-center gap-1.5 px-2 text-[11px] font-medium text-zinc-300 select-none active:cursor-grabbing"
      >
        <GripHorizontal className="h-3.5 w-3.5 shrink-0 text-zinc-500" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{handleLabel}</span>
        {handleExtra}
      </div>
      {children}
    </div>
  )
}
